import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import Dashboard from '@/pages/Dashboard';
import UploadCV from '@/pages/UploadCV';
import CandidatesList from '@/pages/CandidatesList';
import CandidateDetail from '@/pages/CandidateDetail';
import JobsList from '@/pages/JobsList';
import CreateJob from '@/pages/CreateJob';
import JobDetail from '@/pages/JobDetail';
import Certifications from '@/pages/Certifications';
import PersonaManagement from '@/pages/PersonaManagement';
import UserManagement from '@/pages/UserManagement';
import MasterDataManagement from '@/pages/MasterDataManagement';
import TagManagement from '@/pages/TagManagement';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import Navigation from '@/components/Navigation';
import ProtectedRoute from '@/components/ProtectedRoute';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { Toaster } from 'sonner';

function AuthedLayout({ children, pageKey }) {
  return (
    <ProtectedRoute pageKey={pageKey}>
      <Navigation />
      {children}
    </ProtectedRoute>
  );
}

function LandingRedirect() {
  const { pages } = useAuth();
  if (pages.includes('dashboard')) {
    return <Dashboard />;
  }

  const landingPages = [
    { pageKey: 'upload', path: '/upload' },
    { pageKey: 'candidates', path: '/candidates' },
    { pageKey: 'jobs', path: '/jobs' },
    { pageKey: 'certifications', path: '/certifications' },
    { pageKey: 'personas', path: '/personas' },
    { pageKey: 'users', path: '/users' },
    { pageKey: 'master_data', path: '/master-data' },
    { pageKey: 'tags', path: '/tags' },
  ];
  const destination = landingPages.find(({ pageKey }) => pages.includes(pageKey))?.path;

  if (destination) {
    return <Navigate to={destination} replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="no-pages-assigned-screen">
      <div className="text-center max-w-md px-6">
        <p className="text-lg font-medium text-slate-800 mb-2">No pages assigned</p>
        <p className="text-sm text-slate-600">
          Your account does not have access to any pages. Contact an administrator for access.
        </p>
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Protected routes */}
            <Route path="/" element={<AuthedLayout><LandingRedirect /></AuthedLayout>} />
            <Route path="/upload" element={<AuthedLayout pageKey="upload"><UploadCV /></AuthedLayout>} />
            <Route path="/candidates" element={<AuthedLayout pageKey="candidates"><CandidatesList /></AuthedLayout>} />
            <Route path="/candidates/:id" element={<AuthedLayout pageKey="candidates"><CandidateDetail /></AuthedLayout>} />
            <Route path="/jobs" element={<AuthedLayout pageKey="jobs"><JobsList /></AuthedLayout>} />
            <Route path="/jobs/new" element={<AuthedLayout pageKey="jobs"><CreateJob /></AuthedLayout>} />
            <Route path="/jobs/:id" element={<AuthedLayout pageKey="jobs"><JobDetail /></AuthedLayout>} />
            <Route path="/certifications" element={<AuthedLayout pageKey="certifications"><Certifications /></AuthedLayout>} />
            <Route path="/personas" element={<AuthedLayout pageKey="personas"><PersonaManagement /></AuthedLayout>} />
            <Route path="/users" element={<AuthedLayout pageKey="users"><UserManagement /></AuthedLayout>} />
            <Route path="/master-data" element={<AuthedLayout pageKey="master_data"><MasterDataManagement /></AuthedLayout>} />
            <Route path="/tags" element={<AuthedLayout pageKey="tags"><TagManagement /></AuthedLayout>} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
